import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	LinearProgress,
	Paper,
	Snackbar,
	Stack,
	Typography,
} from "@mui/material";
import { GripVertical, ImagePlus, Trash2, Upload } from "lucide-react";
import { supabase } from "../../config/supabase";

const STORAGE_BUCKET = "school-logos";
const TABLE_NAME = "school_logos";

const publicUrlFor = (filePath) => {
	const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(filePath);
	return data?.publicUrl || "";
};

const SchoolLogos = () => {
	const [logos, setLogos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
	const [uploadOpen, setUploadOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [selected, setSelected] = useState(null);
	const [files, setFiles] = useState([]);
	const [dragIndex, setDragIndex] = useState(null);
	const [overIndex, setOverIndex] = useState(null);
	const [savingOrder, setSavingOrder] = useState(false);
	const dragItem = useRef(null);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const showMsg = (message, severity = "success") =>
		setSnackbar({ open: true, message, severity });

	const fetchLogos = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from(TABLE_NAME)
				.select("*")
				.order("sort_order", { ascending: true })
				.order("created_at", { ascending: true });

			if (error) throw error;
			setLogos(data || []);
		} catch (err) {
			console.error("Failed to load school logos:", err);
			showMsg(err.message || "Failed to load school logos", "error");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchLogos();
	}, [fetchLogos]);

	const openUpload = () => {
		setFiles([]);
		setUploadOpen(true);
	};

	const onPickFiles = (fileList) => {
		const picked = Array.from(fileList || []).filter((f) =>
			f.type.startsWith("image/"),
		);
		if ((fileList?.length || 0) > 0 && picked.length === 0) {
			showMsg("Only image files are allowed", "warning");
		}
		setFiles(picked);
	};

	const uploadOne = async (file, sortOrder) => {
		const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
		const filePath = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;

		const { error: uploadError } = await supabase.storage
			.from(STORAGE_BUCKET)
			.upload(filePath, file, {
				contentType: file.type,
				upsert: false,
			});
		if (uploadError) throw uploadError;

		const { error: insertError } = await supabase.from(TABLE_NAME).insert({
			name: null,
			file_path: filePath,
			file_name: file.name,
			sort_order: sortOrder,
		});

		if (insertError) {
			await supabase.storage.from(STORAGE_BUCKET).remove([filePath]);
			throw insertError;
		}
	};

	const handleUpload = async () => {
		if (!files.length) {
			showMsg("Please choose one or more images", "warning");
			return;
		}

		setUploading(true);
		setUploadProgress({ done: 0, total: files.length });
		let success = 0;
		let failed = 0;
		let nextOrder = logos.length;

		try {
			for (const file of files) {
				try {
					await uploadOne(file, nextOrder);
					nextOrder += 1;
					success += 1;
				} catch (err) {
					console.error("Upload failed for", file.name, err);
					failed += 1;
				}
				setUploadProgress((prev) => ({ ...prev, done: prev.done + 1 }));
			}

			if (failed && success) {
				showMsg(
					`Uploaded ${success} logo(s). ${failed} failed.`,
					"warning",
				);
			} else if (failed && !success) {
				showMsg("Upload failed", "error");
			} else {
				showMsg(
					success === 1
						? "Logo uploaded"
						: `${success} logos uploaded`,
				);
			}

			setUploadOpen(false);
			setFiles([]);
			setLoading(true);
			await fetchLogos();
		} finally {
			setUploading(false);
			setUploadProgress({ done: 0, total: 0 });
		}
	};

	const persistOrder = async (ordered) => {
		setSavingOrder(true);
		try {
			for (let i = 0; i < ordered.length; i++) {
				const logo = ordered[i];
				if (logo.sort_order === i) continue;
				const { error } = await supabase
					.from(TABLE_NAME)
					.update({ sort_order: i })
					.eq("id", logo.id);
				if (error) throw error;
			}
			setLogos(ordered.map((logo, i) => ({ ...logo, sort_order: i })));
		} catch (err) {
			console.error("Failed to save order:", err);
			showMsg(err.message || "Failed to save order", "error");
			setLoading(true);
			await fetchLogos();
		} finally {
			setSavingOrder(false);
		}
	};

	const onDragStart = (index) => {
		dragItem.current = index;
		setDragIndex(index);
	};

	const onDragOver = (e, index) => {
		e.preventDefault();
		if (overIndex !== index) setOverIndex(index);
	};

	const onDrop = async (index) => {
		const from = dragItem.current;
		setDragIndex(null);
		setOverIndex(null);
		dragItem.current = null;
		if (from === null || from === undefined || from === index) return;

		const next = [...logos];
		const [moved] = next.splice(from, 1);
		next.splice(index, 0, moved);
		setLogos(next);
		await persistOrder(next);
	};

	const onDragEnd = () => {
		setDragIndex(null);
		setOverIndex(null);
		dragItem.current = null;
	};

	const handleDelete = async () => {
		if (!selected) return;
		setUploading(true);
		try {
			await supabase.storage.from(STORAGE_BUCKET).remove([selected.file_path]);
			const { error } = await supabase
				.from(TABLE_NAME)
				.delete()
				.eq("id", selected.id);
			if (error) throw error;
			showMsg("Logo deleted");
			setDeleteOpen(false);
			setSelected(null);
			setLoading(true);
			await fetchLogos();
		} catch (err) {
			console.error("Delete failed:", err);
			showMsg(err.message || "Failed to delete logo", "error");
		} finally {
			setUploading(false);
		}
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={2}
				sx={{ mb: 1 }}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<ImagePlus size={28} />
					<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
						School Logos
					</Typography>
				</Stack>
				<Button
					variant="contained"
					startIcon={<Upload size={16} />}
					onClick={openUpload}>
					Upload logos
				</Button>
			</Stack>

			<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
				Upload school logos for the auto-scrolling strip on the public website.
				Drag cards to rearrange display order.
				{savingOrder ? " Saving order…" : ""}
			</Typography>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : logos.length === 0 ? (
				<Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
					<Typography color="text.secondary">
						No logos yet. Upload one or more school logos to get started.
					</Typography>
				</Paper>
			) : (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
						gap: 2,
					}}>
					{logos.map((logo, index) => (
						<Paper
							key={logo.id}
							draggable
							onDragStart={() => onDragStart(index)}
							onDragOver={(e) => onDragOver(e, index)}
							onDrop={() => onDrop(index)}
							onDragEnd={onDragEnd}
							sx={{
								p: 2,
								borderRadius: 2,
								display: "flex",
								flexDirection: "column",
								gap: 1.5,
								cursor: "grab",
								opacity: dragIndex === index ? 0.55 : 1,
								outline:
									overIndex === index && dragIndex !== index
										? "2px solid"
										: "none",
								outlineColor: "primary.main",
								"&:active": { cursor: "grabbing" },
							}}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between">
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<GripVertical size={16} />
									<Typography variant="caption" color="text.secondary">
										#{index + 1}
									</Typography>
								</Stack>
								<IconButton
									color="error"
									size="small"
									aria-label="Delete logo"
									onClick={(e) => {
										e.stopPropagation();
										setSelected(logo);
										setDeleteOpen(true);
									}}
									onMouseDown={(e) => e.stopPropagation()}>
									<Trash2 size={16} />
								</IconButton>
							</Stack>
							<Box
								sx={{
									height: 110,
									display: "grid",
									placeItems: "center",
									bgcolor: "grey.50",
									borderRadius: 1,
									border: "1px solid",
									borderColor: "divider",
									overflow: "hidden",
									px: 1,
									pointerEvents: "none",
								}}>
								<img
									src={publicUrlFor(logo.file_path)}
									alt={logo.file_name}
									style={{
										maxWidth: "100%",
										maxHeight: "100%",
										objectFit: "contain",
									}}
								/>
							</Box>
							<Typography variant="caption" noWrap title={logo.file_name}>
								{logo.file_name}
							</Typography>
						</Paper>
					))}
				</Box>
			)}

			<Dialog
				open={uploadOpen}
				onClose={() => !uploading && setUploadOpen(false)}
				fullWidth
				maxWidth="sm">
				<DialogTitle>Upload school logos</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Button variant="outlined" component="label" disabled={uploading}>
							{files.length
								? `${files.length} image${files.length > 1 ? "s" : ""} selected`
								: "Choose images"}
							<input
								hidden
								type="file"
								accept="image/*"
								multiple
								onChange={(e) => onPickFiles(e.target.files)}
							/>
						</Button>
						{files.length > 0 && (
							<Box
								sx={{
									maxHeight: 160,
									overflow: "auto",
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
									px: 1.5,
									py: 1,
								}}>
								{files.map((file) => (
									<Typography key={file.name + file.size} variant="body2" noWrap>
										{file.name}
									</Typography>
								))}
							</Box>
						)}
						{uploading && (
							<Box>
								<Typography variant="caption" color="text.secondary">
									Uploading {uploadProgress.done} / {uploadProgress.total}
								</Typography>
								<LinearProgress
									variant="determinate"
									value={
										uploadProgress.total
											? (uploadProgress.done / uploadProgress.total) * 100
											: 0
									}
									sx={{ mt: 1 }}
								/>
							</Box>
						)}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setUploadOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleUpload}
						disabled={uploading || files.length === 0}
						startIcon={
							uploading ? <CircularProgress size={16} /> : <Upload size={16} />
						}>
						{uploading
							? "Uploading..."
							: files.length > 1
								? `Upload ${files.length}`
								: "Upload"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteOpen}
				onClose={() => !uploading && setDeleteOpen(false)}>
				<DialogTitle>Delete logo?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This removes <strong>{selected?.file_name}</strong> from the website
						and deletes the file from storage.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleDelete}
						disabled={uploading}>
						{uploading ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default SchoolLogos;
